import { z } from "zod"

import type { RuleFieldChange } from "~/utils/replenishmentRules"
import {
  DEFAULT_CUTOFF_MINUTES,
  reviewPeriodDays,
  reviewPeriodRange,
  type RuleDepartureSchedule,
} from "~/utils/allocationRuleMaths"
import { rruleDayToWeekday } from "~/utils/rruleWeekdays"

const RULE_WEEKDAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const
const DEFAULT_RULE_CUTOFF = `${String(DEFAULT_CUTOFF_MINUTES / 60).padStart(2, "0")}:00`

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

/** The AllocationRule columns a planner edits, in the shape they are stored. */
export const ruleValuesSchema = z.object({
  name: z.string().max(80),
  description: z.string().max(280).nullable(),
  unit: z.enum(["day", "quantity"]),
  minStock: z.number().int().min(0),
  upToStock: z.number().int().min(0),
  maxStock: z.number().int().min(0),
  safetyStockDays: z.number().int().min(0).max(365),
  freq: z.enum(["DAILY", "WEEKLY"]),
  byDay: z.array(z.enum(RULE_WEEKDAYS)).max(7),
  interval: z.number().int().min(1).max(365),
  /** "YYYY-MM-DD"; null lets saving anchor the schedule on the next possible departure. */
  dtstart: isoDate.nullable(),
  /** "HH:MM" in UTC. */
  cutoff: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
})
export type RuleValues = z.infer<typeof ruleValuesSchema>

/** The editable values of a stored or fetched rule, without its id and audit columns. */
export function ruleValuesOf(rule: RuleValues): RuleValues {
  return {
    name: rule.name,
    description: rule.description,
    unit: rule.unit,
    minStock: rule.minStock,
    upToStock: rule.upToStock,
    maxStock: rule.maxStock,
    safetyStockDays: rule.safetyStockDays,
    freq: rule.freq,
    byDay: rule.byDay,
    interval: rule.interval,
    dtstart: rule.dtstart,
    cutoff: rule.cutoff,
  }
}

/** The id the editor gives an unsaved rule. A save's store moves onto it land on the created rule. */
export const NEW_RULE_ID = "__new__"

export const NEW_RULE_VALUES: RuleValues = {
  name: "Untitled rule",
  description: null,
  unit: "day",
  minStock: 0,
  upToStock: 0,
  maxStock: 0,
  safetyStockDays: 4,
  freq: "WEEKLY",
  byDay: ["MO"],
  interval: 1,
  dtstart: null,
  cutoff: DEFAULT_RULE_CUTOFF,
}

/** The first problem that stops a rule from being saved, or null. */
export function ruleValuesProblem(values: RuleValues): string | null {
  if (values.name.trim().length < 3) return "A rule name needs at least 3 characters."
  if (values.freq === "DAILY" && values.byDay.length > 0) {
    return "A review period in days has no departure calendar."
  }
  if (values.freq === "WEEKLY" && values.byDay.length === 0) {
    return "Pick at least one departure day."
  }
  if (values.freq === "WEEKLY" && values.interval > 12) {
    return "A weekly review period repeats at most every 12 weeks."
  }
  if (values.unit === "day" && values.maxStock <= 0) {
    return "Set an overstock cover above 0 days."
  }
  if (values.unit === "quantity" && values.minStock > values.upToStock) {
    return "The reorder point cannot be above the order-up-to level."
  }
  if (values.unit === "quantity" && values.upToStock > values.maxStock) {
    return "The order-up-to level cannot be above the overstock level."
  }
  return null
}

const sortedDays = (byDay: readonly string[]) => RULE_WEEKDAYS.filter((day) => byDay.includes(day))

export function sameSchedule(
  a: Pick<RuleValues, "freq" | "byDay" | "interval">,
  b: Pick<RuleValues, "freq" | "byDay" | "interval">,
) {
  return (
    a.freq === b.freq &&
    a.interval === b.interval &&
    sortedDays(a.byDay).join(",") === sortedDays(b.byDay).join(",")
  )
}

const BASIS_LABEL: Record<RuleValues["unit"], string> = {
  day: "Days of stock",
  quantity: "Unit quantity",
}

const dec1 = (n: number) => n.toFixed(1)

/** "3.5 d" when every gap between departures is the same, "1 to 6 d" when they differ. */
export function reviewPeriodText(rule: RuleDepartureSchedule): string {
  const { min, max } = reviewPeriodRange(rule)
  return min === max ? `${dec1(reviewPeriodDays(rule))} d` : `${min} to ${max} d`
}
const orNone = (value: string | null | undefined) =>
  value == null || value === "" ? "None" : value
const calendarLabel = (byDay: readonly string[]) =>
  sortedDays(byDay).map(rruleDayToWeekday).join("/") || "None"

type Row = Omit<RuleFieldChange, "before" | "after"> & {
  value: (rule: RuleValues) => string
  /** Only listed while one side reads the field. */
  when?: (rule: RuleValues) => boolean
}

const isQuantity = (rule: RuleValues) => rule.unit === "quantity"

// Labels and field codes from docs/ALLOCATION_PLANNING_TERMINOLOGY.md.
const ROWS: Row[] = [
  { field: "name", label: "Name", value: (rule) => rule.name.trim() },
  { field: "description", label: "Description", value: (rule) => orNone(rule.description) },
  { field: "threshold_mode", label: "Targets based on", value: (rule) => BASIS_LABEL[rule.unit] },
  {
    field: "reorder_point",
    label: "Reorder point",
    value: (rule) => `${rule.minStock} u`,
    when: isQuantity,
  },
  {
    field: "out_level",
    label: "Order-up-to level",
    value: (rule) => `${rule.upToStock} u`,
    when: isQuantity,
  },
  {
    field: "overstock_level",
    label: "Overstock level",
    value: (rule) => `${rule.maxStock} u`,
    when: isQuantity,
  },
  {
    field: "cover_overstock",
    label: "Overstock cover (days)",
    value: (rule) => `${rule.maxStock} d`,
    when: (rule) => !isQuantity(rule),
  },
  {
    field: "safety_cover_days",
    label: "Safety stock (days)",
    value: (rule) => `${rule.safetyStockDays} d`,
  },
  {
    field: "review_period_days",
    label: "Review period (days)",
    value: reviewPeriodText,
  },
  {
    field: "delivery_days",
    label: "Departure calendar",
    value: (rule) => calendarLabel(rule.byDay),
  },
  { field: "dtstart", label: "Start date", value: (rule) => orNone(rule.dtstart) },
  { field: "cutoff_time", label: "Cut-off time (UTC)", value: (rule) => rule.cutoff },
]

/** Field changes from one stored rule version to the next, in audit-log shape. */
export function diffRule(before: RuleValues, after: RuleValues): RuleFieldChange[] {
  const changes: RuleFieldChange[] = []
  for (const row of ROWS) {
    if (row.when && !row.when(before) && !row.when(after)) continue
    const from = row.value(before)
    const to = row.value(after)
    if (from !== to) changes.push({ field: row.field, label: row.label, before: from, after: to })
  }
  return changes
}

/** False when only the name or description changed, which the engine never reads. */
export function changesSuggestions(changes: RuleFieldChange[]) {
  return changes.some((change) => change.field !== "name" && change.field !== "description")
}
